import { IAttachmentWithData } from '@alberta/konexi-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { TenantConfigService } from '@services/tenant-config/tenant-config.service';
import { fabric } from 'fabric';
import { assignIn } from 'lodash';
import { AuditNodeViewModel } from 'src/app/business/swodoc/view-models/audit-node-view-model';
import { TreeArray } from 'src/app/common/tree-array/tree-array';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { AuditNodeDto } from 'src/app/shared/models/swodoc/audit-node-dto.model';
import { DocumentEditService } from '../../../document-edit.service';
import { ResizeableLine } from '../fabric-canvas/fabric-resizeable-line';
import { Point } from '../polygon/point.interface';
import { calcArea, getBoundingBox, getLineLength } from '../polygon/polygon.helper';

export enum FabricMode {
  Draw = 'draw',
  Measure = 'measure',
}


@Component({
  selector: 'itl-fabric-modal',
  templateUrl: './fabric.modal.html',
  styleUrls: ['./fabric.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FabricModal implements OnInit {
  @Input()
  public currentIndex = 0;
  public isAndroid: boolean;
  public version: number;
  public imageInput: Partial<IAttachmentWithData>;
  @Input() node: AuditNodeViewModel;
  @Input() auditNodes: AuditNodeViewModel[];
  @Input() documentEditService: DocumentEditService;
  public tenantScales: SelectionItem[];
  public scaleSelectionDisabled = false;

  public get zoomInDisabled(): boolean {
    return this._canvas?.getZoom() === 2.5;
  }

  public get zoomOutDisabled(): boolean {
    return this._canvas?.getZoom() === 0.5;
  }


  // POLYGON DATA
  public image = new Image();
  private _isCanvasDrawn = true;
  private _canvas: fabric.Canvas;
  private _polygon: fabric.Polygon;
  private _isImageDrawn = false;
  private _isPolygonDrawn = false;

  // dragging
  private _isDragging = false;
  private _lastPosX: number;
  private _lastPosY: number;

  // points
  private _points: Point[] = [];
  private _lastPoint: Point;
  private _isPolygonDrawing = false;

  // class-member
  private _line: ResizeableLine;
  private _boundingBox: fabric.Rect;

  // number values
  private _woundAreaMm = 0;
  private _woundHeightMm = 0;
  private _woundWidthMm = 0;
  private _calculateScale = 0; // = 1 / 8000; cm : pixel

  public selectedScaleInMillimeter: number;

  private _canvasOriginalHeight: number;
  private _canvasOriginalWidth: number;

  // string values
  public woundHeightStringCm = '';
  public woundWidthStringCm = '';
  public woundAreaStringCm = '';

  public FabricMode = FabricMode;
  public drawMode = false;
  public woundSelected = false;
  public InfoBoxType = InfoBoxType;

  public get isIos(): boolean {
    return this._platform.is('ios');
  }

  public get isValueMissing(): boolean {
    return !this._woundHeightMm || !this._woundWidthMm || !this._woundAreaMm;
  }

  constructor(
    public _modalCtrl: ModalController,
    private _platform: Platform,
    private _changeDetectionRef: ChangeDetectorRef,
    private _tenantConfigServie: TenantConfigService
  ) {
    const defaultOnTouchStartHandler = (fabric.Canvas.prototype as any)._onTouchStart;
    fabric.util.object.extend(fabric.Canvas.prototype, {
      _onTouchStart: function (e: any) {
        const target = this.findTarget(e);
        // if allowTouchScrolling is enabled, no object was at the
        // the touch position and we're not in drawing mode, then
        // let the event skip the fabricjs canvas and do default
        // behavior
        if (this.allowTouchScrolling && !target && !this.isDrawingMode) {
          // returning here should allow the event to propagate and be handled
          // normally by the browser
          return;
        }

        // otherwise call the default behavior
        defaultOnTouchStartHandler.call(this, e);
      },
    });
  }

  async ngOnInit(): Promise<void> {
    this.selectedScaleInMillimeter = 100
    this.scaleSelectionDisabled = true;
    this.tenantScales = [new SelectionItem(100, "10 cm")]
    this.tenantScales = await this._tenantConfigServie.getWoundScales().then(
      (woundScales) => {
        if(woundScales){
          this.selectedScaleInMillimeter = woundScales[0] 
          this.scaleSelectionDisabled = false;
          return woundScales.map((scale) => {
            const scaleInCmString = (scale / 10).toString() + " cm"
            return new SelectionItem(scale, scaleInCmString)
          })
        }
      }
    )
    this.initializeCanvas();
  }

  public scaleChanged(scale)
  {
    this.selectedScaleInMillimeter = scale;
    this.resetDrawing()
  }

  private initializeCanvas(): void {
    this._initImage();
    this.selectFile();
    this._afterInitImage();
  }

  private _initImage(): void {
    // POLYGON INIT
    this._canvas = new fabric.Canvas('woundMeasureCanvas', {
      selectionBorderColor: 'transparent',
      selectionColor: 'transparent',
      allowTouchScrolling: true,
    });

    // scale canvas absolut
    this._sizeCanvas();

    this._canvas.selectionBorderColor = 'transparent';
    this._canvas.selectionColor = 'transparent';

    const canvasWidth = this._canvas.width;
    const canvasHeight = this._canvas.height;
    const lineStartX = canvasWidth * 0.3;
    const lineEndX = canvasWidth * 0.7;
    const lineY = canvasHeight * 0.1;

    this._line = new ResizeableLine([lineStartX, lineY, lineEndX, lineY], {
      strokeWidth: 2,
      stroke: 'white',
      strokeLineCap: 'round',
      transparentCorners: false,
      cornerStyle: 'circle',
      cornerSize: 15,
      cornerColor: '#00B293',
      cornerStrokeColor: 'white',
      shadow: new fabric.Shadow({
        color: 'rgba(0,0,0,0.2)',
        blur: 2,
        offsetX: 0,
        offsetY: 0,
      }),
    });

    this._boundingBox = new fabric.Rect({
      originX: 'left',
      originY: 'bottom',
      left: 0,
      top: 0,
      height: 0,
      width: 0,
      stroke: 'green',
      fill: '#22334480',
      angle: 0,
      selectable: false,
      evented: false,
    });

    this._polygon = new fabric.Polygon(this._points, {
      left: 0,
      top: 0,
      fill: 'rgba(255, 255, 255, 0.3)',
      strokeWidth: 2,
      stroke: 'rgba(255, 255, 255, 0.7)',
      strokeDashArray: [2, 4],
      strokeLineCap: 'round',
      scaleX: 1,
      scaleY: 1,
      objectCaching: false,
      transparentCorners: false,
      cornerStyle: 'circle',
      cornerColor: 'rgba(0, 153, 126, 1)',
      cornerStrokeColor: 'rgba(255, 255, 255, 1)',
      borderColor: 'transparent',
    });

    this._canvas.viewportTransform = [1, 0, 0, 1, 0, 0];

    this._canvas.add(this._line);
    this._canvas.add(this._boundingBox);

    this._line.on('modified', () => {
      this.setScale();
    });

    this._canvas.setActiveObject(this._line);
  }

  private _sizeCanvas(): void {
    const viewportWidth = document.getElementsByClassName('wizard-page__content')[0]
      ? document.getElementsByClassName('wizard-page__content')[0].clientWidth
      : window.innerWidth;
    const viewportHeight = document.getElementsByClassName('wizard-page__content')[0]
      ? document.getElementsByClassName('wizard-page__content')[0].clientHeight - 100
      : window.innerHeight;
    this._canvas.setWidth(viewportWidth);
    this._canvas.setHeight(viewportHeight);
    this._canvasOriginalHeight = this._canvas.getHeight();
    this._canvasOriginalWidth = this._canvas.getWidth();
  }

  private _emitMeasureData(saveValues = false): boolean {
    if (this.isValueMissing) {
      return false;
    }

    this.woundAreaStringCm = (this._woundAreaMm / 100).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
    this.woundHeightStringCm = (this._woundHeightMm / 10).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
    this.woundWidthStringCm = (this._woundWidthMm / 10).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

    this._changeDetectionRef.detectChanges();

    // write data to other children elements here
    if (saveValues) {
      const treeArray = assignIn(new TreeArray<AuditNodeDto>(), this.auditNodes);

      const woundWithNode = treeArray.findRec(item => {
        return item.globalType === '148'; // Wundgröße Breite
      });
      const woundHeightNode = treeArray.findRec(item => {
        return item.globalType === '149'; // Wundgröße Länge
      });
      const woundAreaNode = treeArray.findRec(item => {
        return item.globalType === '181' // Wundgröße Fläche
      });

      if (woundWithNode) {
        woundWithNode.responses = [
          {
            value: this._woundWidthMm.toLocaleString('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            })
          },
        ];
        (woundWithNode as any).updateNode = new Date();
      }
      if (woundHeightNode) {
        woundHeightNode.responses = [
          {
            value: this._woundHeightMm.toLocaleString('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            })
          },
        ];
        (woundHeightNode as any).updateNode = new Date();
      }
      if (woundAreaNode) {
        woundAreaNode.responses = [
          {
            value: this._woundAreaMm.toLocaleString('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            })
          },
        ];
        (woundAreaNode as any).updateNode = new Date();
      }
    }
    this.woundSelected = true;
    this._changeDetectionRef.detectChanges();
    return true;
  }

  public saveValuesAndCloseModal(): void {
    if (this._emitMeasureData(true)) {
      this._modalCtrl.dismiss();
    }
  }

  public switchMode(mode: FabricMode) {
    if (mode === FabricMode.Draw) {
      this.drawMode = true;
      this._canvas.allowTouchScrolling = false;
      this._canvas.discardActiveObject();
      this._canvas.setActiveObject(this._polygon);
      this._line.selectable = false;
      this._line.hoverCursor = 'default';
      this._polygon.set({
        strokeDashArray: this._isPolygonDrawn ? null : [2, 4],
        hasControls: true,
        selectable: true,
        evented: true,
      });
    } else if (mode === FabricMode.Measure) {
      this.drawMode = false;
      this._canvas.setActiveObject(this._line);
      this._canvas.allowTouchScrolling = true;
      this._line.selectable = true;
      this._line.hoverCursor = 'move';
      this._polygon.set({
        strokeDashArray: null,
        hasControls: false,
        selectable: false,
        evented: false,
      });
    }
    this._canvas.renderAll();
  }

  public zoomIn() {
    if (this._canvas.getZoom() < 3) {
      this._canvas.setZoom(this._canvas.getZoom() + 0.5);
      this._canvas.setWidth(this._canvasOriginalWidth * this._canvas.getZoom());
      this._canvas.setHeight(this._canvasOriginalHeight * this._canvas.getZoom());
      //Center the canvas within its parent div
      const parentDiv = document.querySelector('.woundContainer');
      parentDiv.scrollLeft = (this._canvas.getWidth() - parentDiv.clientWidth) / 2;
    }
  }

  public zoomOut() {
    const newZoom = this._canvas.getZoom() - 0.5;
    if (newZoom > 0) {
      this._canvas.setZoom(this._canvas.getZoom() - 0.5);
      this._canvas.setWidth(this._canvasOriginalWidth * this._canvas.getZoom());
      this._canvas.setHeight(this._canvasOriginalHeight * this._canvas.getZoom());
      // Center the canvas within its parent div
      const parentDiv = document.querySelector('.woundContainer');
      parentDiv.scrollLeft = (this._canvas.getWidth() - parentDiv.clientWidth) / 2;
    }
  }

  private _setBoundingBox(): void {
    // As the polygon.points only contain the initial coordinatea,
    // we need to transform those to their actual position
    const matrix = this._polygon.calcTransformMatrix();
    const points = this._polygon.points
      .map(p => new fabric.Point(p.x - this._polygon.pathOffset.x, p.y - this._polygon.pathOffset.y))
      .map(p => fabric.util.transformPoint(p, matrix));

    const boundingBox = getBoundingBox(points);
    this._woundHeightMm = boundingBox.height * this._calculateScale * this.selectedScaleInMillimeter; // in mm
    this._woundWidthMm = boundingBox.width * this._calculateScale * this.selectedScaleInMillimeter; // in mm

    this._emitMeasureData();

    this._boundingBox.set({
      originX: 'left',
      originY: 'bottom',
      left: boundingBox.origin.x,
      top: boundingBox.origin.y,
      height: boundingBox.height,
      width: boundingBox.width,
      angle: boundingBox.angleDeg,
      visible: false,
    });
  }

  private _afterInitImage(): void {
    // inital scale
    this.setScale();

    this._canvas.on('object:modified', event => {
      if (event.target === this._polygon) {
        this._calcPolygonArea();
      }
    });

    this._canvas.on('object:moving', event => {
      if (event.target === this._polygon) {
        this._boundingBox.visible = false;
      }
    });
    // showing polygon corners when clicking outside of polygon
    this._canvas.on('selection:cleared', () => {
      if (this.drawMode && this._isPolygonDrawn) {
        this._canvas.setActiveObject(this._polygon);
        this._canvas.renderAll();
      }
    });

    this._canvas.on('mouse:up', options => {
      if (this._isPolygonDrawing) {
        this._isPolygonDrawing = false;

        this._makePolygon();
        this._isPolygonDrawn = true;

        this._polygon.set({
          strokeDashArray: null,
          hasControls: true,
          selectable: true,
          evented: true,
        });

        this._canvas.renderAll();
        this._canvas.setActiveObject(this._polygon);
        this._calcPolygonArea();
        this._editPolygon();
        // this is essential, to make the  polygon selectable and resizable
        (this._polygon as any)._setPositionDimensions({});
      }

      // end dragging
      this._canvas.setViewportTransform(this._canvas.viewportTransform);
      this._isDragging = false;
      this._canvas.selection = true;

      this._canvas.renderAll();
    });

    this._canvas.on('mouse:move', options => {
      const event = options.e;
      const pointer = this._canvas.getPointer(event);

      if (this._isDragging) {
        const vpt = this._canvas.viewportTransform;
        vpt[4] += event.clientX - this._lastPosX;
        vpt[5] += event.clientY - this._lastPosY;
        this._canvas.requestRenderAll();
        this._lastPosX = event.clientX;
        this._lastPosY = event.clientY;
      }

      if (this._isPolygonDrawing && getLineLength(pointer, this._lastPoint) > 20 && options.target !== this._line) {
        this._setPointCoords(pointer);
        this._lastPoint = pointer;
      }
    });

    this._canvas.on('mouse:down', event => {
      if (event.e.ctrlKey === true) {
        this._isDragging = true;
        this._canvas.selection = false;
        this._lastPosX = event.e.clientX;
        this._lastPosY = event.e.clientY;
      }
      if (event.button === 1 && this.drawMode) {
        const pointer = this._canvas.getPointer(event.e);
        if (!this._isPolygonDrawn && event.target !== this._line) {
          this._points.push(pointer);
          this._lastPoint = pointer;
          this._isPolygonDrawing = true;
          this._canvas.add(this._polygon);
        } else if (event.target === this._line) {
          this._lastPoint = pointer;
          this._canvas.add(this._line);
        }
      }
    });
  }

  private selectFile(): void {
    const canvas = this._canvas;

    if (this.imageInput) {
      fabric.Image.fromURL(this.imageInput.data, img => {
        const imgRatioHeight = canvas.height / img.height;
        const scalingFactorHeight = imgRatioHeight > 1 ? 1 : imgRatioHeight;
        const imgRatioWidth = canvas.width / img.width;
        const scalingFactorWidth = imgRatioWidth > 1 ? 1 : imgRatioWidth;
        const scalingFactor = scalingFactorHeight > scalingFactorWidth ? scalingFactorWidth : scalingFactorHeight;

        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          scaleX: scalingFactor,
          scaleY: scalingFactor,
        });
        canvas.centerObject(img);
      });
      this._isImageDrawn = true;
    }
  }

  private _setPointCoords(point: Point) {
    if (this._isCanvasDrawn && this._isImageDrawn) {
      this._points.push({
        x: point.x,
        y: point.y,
      });
      this._canvas.renderAll();
    }
  }

  private _makePolygon(): void {
    this._isImageDrawn = false;
  }

  private _editPolygon(): void {
    function polygonPositionHandler(dim, finalMatrix, fabricObject: fabric.Polygon) {
      const x = fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x,
        y = fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y;
      return fabric.util.transformPoint(
        new fabric.Point(x, y),
        fabric.util.multiplyTransformMatrices(fabricObject.canvas.viewportTransform, fabricObject.calcTransformMatrix())
      );
    }
    function anchorWrapper(anchorIndex: number, fn, self) {
      return function (eventData, transform, x: number, y: number) {
        const fabricObject: fabric.Polygon = transform.target,
          absolutePoint = fabric.util.transformPoint(
            new fabric.Point(
              fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x,
              fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y
            ),
            fabricObject.calcTransformMatrix()
          ),
          actionPerformed = fn(eventData, transform, x, y, self),
          newDim = (fabricObject as any)._setPositionDimensions({}),
          polygonBaseSize = fabricObject._getNonTransformedDimensions(),
          newX = (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) / polygonBaseSize.x,
          newY = (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) / polygonBaseSize.y;
        (fabricObject as any).setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
        return actionPerformed;
      };
    }
    function actionHandler(eventData, transform, x: number, y: number, self) {
      // hide boundingBox on repositioning a point
      self._boundingBox.visible = false;

      const polygon: fabric.Polygon = transform.target,
        currentControl = polygon.controls[(polygon as any).__corner],
        mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center'),
        polygonBaseSize = polygon._getNonTransformedDimensions(),
        size = polygon._getTransformedDimensions(0, 0),
        finalPointPosition = {
          x: (mouseLocalPosition.x * polygonBaseSize.x) / size.x + polygon.pathOffset.x,
          y: (mouseLocalPosition.y * polygonBaseSize.y) / size.y + polygon.pathOffset.y,
        } as fabric.Point;
      polygon.points[(currentControl as any).pointIndex] = finalPointPosition;

      return true;
    }
    const poly: fabric.Polygon = this._canvas.getObjects('polygon')[0] as fabric.Polygon;
    this._canvas.setActiveObject(poly);

    const lastControl = poly.points.length - 1;
    poly.controls = poly.points.reduce(
      function (acc, point, index: number) {
        acc['p' + index] = new fabric.Control({
          pointIndex: index,
          positionHandler: polygonPositionHandler,
          actionHandler: anchorWrapper(index > 0 ? index - 1 : lastControl, actionHandler, this),
          actionName: 'modifyPolygon',
        } as any);
        return acc;
      }.bind(this),
      {}
    );

    this._canvas.requestRenderAll();
  }

  private _calcPolygonArea(): boolean {
    const area = calcArea(this._points) * Math.pow(this._calculateScale, 2) * this.selectedScaleInMillimeter * this.selectedScaleInMillimeter; // in mm
    if (area === 0 || !area) {
      return false;
    }
    this._woundAreaMm = area;
    this._emitMeasureData();
    this._setBoundingBox();
    return true;
  }

  public setScale(): void {
    // set temp
    this._calculateScale = 1 / this._line.length;

    this._calcPolygonArea();
  }

  public resetDrawing(): void {
    this._canvas.clear();
    this._canvas.dispose();

    this._points = [];
    this._woundAreaMm = 0;
    this._woundHeightMm = 0;
    this._woundWidthMm = 0;
    this.woundAreaStringCm = '';
    this.woundHeightStringCm = '';
    this.woundWidthStringCm = '';
    this._isPolygonDrawn = false;
    this.drawMode = false;
    this.woundSelected = false;

    this.initializeCanvas();
    this.switchMode(FabricMode.Measure);
  }
  
}
